import { useCallback, useEffect, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { adminFetch, AdminApiError } from '@/providers/admin-api';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import { toast } from '@/hooks/useToast';
import { formatDate } from '@/lib/utils';

interface Note {
  id: string;
  note: string;
  created_at: string;
  admin_email: string | null;
  admin_name: string | null;
}

/**
 * Internal admin notes on a user (user-360). Self-contained — fetches its own
 * thread and gates on `users.notes.manage` (returns null when the admin lacks
 * it, so the card never renders for unauthorized roles). Backed by
 * GET/POST /v1/admin/users/:id/notes (migration 216).
 */
export function UserNotesCard({ userId }: { userId: string }) {
  const apiUrl = useApiUrl();
  const { hasPermission } = useAdminPermissions();
  const canManage = hasPermission('users.notes.manage');

  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminFetch(`${apiUrl}/v1/admin/users/${userId}/notes`);
      const json = (await r.json()) as { notes: Note[] };
      setNotes(json.notes ?? []);
    } catch {
      /* non-fatal — render empty state */
    } finally {
      setLoading(false);
    }
  }, [apiUrl, userId]);

  useEffect(() => {
    if (canManage) load();
  }, [canManage, load]);

  if (!canManage) return null;

  const submit = async () => {
    const text = draft.trim();
    if (!text) return;
    setSubmitting(true);
    try {
      await adminFetch(`${apiUrl}/v1/admin/users/${userId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ note: text }),
      });
      setDraft('');
      toast({ title: 'Note added' });
      await load();
    } catch (e) {
      toast({
        title: 'Failed to add note',
        description: e instanceof AdminApiError ? e.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-lg border bg-card p-6 lg:col-span-2">
      <h3 className="mb-4 text-lg font-semibold">Internal notes</h3>

      <div className="mb-4">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add an internal note about this user (visible to admins only)…"
          rows={2}
          maxLength={5000}
          className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !draft.trim()}
            className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Adding…' : 'Add note'}
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading notes…</p>
      ) : notes.length === 0 ? (
        <p className="text-sm italic text-muted-foreground">No notes yet.</p>
      ) : (
        <ul className="space-y-3">
          {notes.map((n) => (
            <li key={n.id} className="rounded-lg bg-muted/40 p-3">
              <p className="whitespace-pre-wrap text-sm text-foreground">{n.note}</p>
              <p className="mt-1.5 text-xs text-muted-foreground">
                {n.admin_name || n.admin_email || 'admin'} · {formatDate(n.created_at)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
